import type { CustomerAccountRow } from '../domain/customerTypes';

/**
 * Niveles de dunning (escalada de cobranza).
 * - none: sin deuda o sin vencimiento configurado
 * - r1: vencido hace 1–15 días
 * - r2: vencido hace 16–30 días
 * - urgent: vencido hace más de 30 días
 */
export type DunningLevel = 'none' | 'r1' | 'r2' | 'urgent';

/**
 * Calcula la fecha de vencimiento estimada de un cliente.
 * Base: `debtSince` (cargo más viejo impago, estable ante ediciones de perfil)
 * con fallback a `updatedAt` para datos sin movimientos en el snapshot.
 * Plazo: `paymentTermsDays`.
 * Retorna null si no hay deuda, no hay fecha base o `paymentTermsDays` <= 0.
 */
export function getDueDateIso(customer: CustomerAccountRow): string | null {
  if (customer.balance <= 0) return null;
  const days = customer.paymentTermsDays;
  if (!days || days <= 0) return null;
  const base = customer.debtSince ?? customer.updatedAt;
  if (!base) return null;
  const dt = new Date(base);
  if (isNaN(dt.getTime())) return null;
  dt.setDate(dt.getDate() + days);
  return dt.toISOString();
}

/**
 * Días de mora (positivo = vencido; negativo = quedan N días).
 * Retorna null si no hay fecha de vencimiento calculable.
 */
export function getOverdueDays(customer: CustomerAccountRow): number | null {
  const dueIso = getDueDateIso(customer);
  if (!dueIso) return null;
  const dueMs = new Date(dueIso).getTime();
  const nowMs = Date.now();
  return Math.floor((nowMs - dueMs) / (1000 * 60 * 60 * 24));
}

/**
 * Verdadero si el cliente tiene deuda y está vencido (overdueDays >= 0).
 */
export function isCustomerOverdue(customer: CustomerAccountRow): boolean {
  const days = getOverdueDays(customer);
  return days !== null && days >= 0;
}

/**
 * Nivel de dunning calculado a partir de días de mora.
 */
export function getDunningLevel(customer: CustomerAccountRow): DunningLevel {
  const days = getOverdueDays(customer);
  if (days === null || days < 0) return 'none';
  if (days <= 15) return 'r1';
  if (days <= 30) return 'r2';
  return 'urgent';
}

/** Etiqueta UI corta para cada nivel. */
export const DUNNING_LEVEL_LABEL: Record<DunningLevel, string> = {
  none: '',
  r1: 'Vencido',
  r2: 'Vencido',
  urgent: 'Vencido',
};

/** Color de badge para cada nivel (usa tokens del tema). */
export const DUNNING_LEVEL_TONE: Record<DunningLevel, 'warning' | 'danger' | 'none'> = {
  none: 'none',
  r1: 'warning',
  r2: 'danger',
  urgent: 'danger',
};

export function formatOverdueDaysLabel(days: number): string {
  return days === 1 ? '1 día' : `${days} días`;
}

/** WhatsApp al cliente con deuda vencida. Vos, tono directo. */
export function buildCustomerOverdueWhatsApp(params: {
  name: string;
  balanceLabel: string;
  overdueDays: number;
  level?: DunningLevel;
}): string {
  const daysLabel = formatOverdueDaysLabel(params.overdueDays);
  const { name, balanceLabel } = params;
  const level = params.level ?? 'r1';
  if (level === 'urgent') {
    return `Hola ${name}, tenés ${balanceLabel} de deuda, hace ${daysLabel}. Pasá a pagar o avisame para arreglar.`;
  }
  if (level === 'r2') {
    return `Hola ${name}, tu deuda de ${balanceLabel} lleva ${daysLabel}. ¿Pasás a pagar esta semana?`;
  }
  return `Hola ${name}, tenés ${balanceLabel} de deuda pendiente, hace ${daysLabel}. ¿Cuándo pasás a pagar?`;
}
